import { Action, combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit'
import { PreloadedState } from 'redux'
import clinicContextReducer from '../pages/NewScriptPage/ScriptForm/ClinicContextSlice'
import organizationAdminReducer from '../features/organization-admin/organizationAdminSlice'
import organizationUsersReducer from '../features/users/UsersSlice'
import patientContextReducer from '../features/patient/PatientContextSlice'
import prescriberReducer from '../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import productSearchContextReducer from '../pages/NewScriptPage/ScriptForm/ProductSearchContextSlice'
import scriptReducer from '../pages/NewScriptPage/ScriptForm/PrescribedItem/PrescribedItemSlice'
import organizationReducer from '../pages/OrganizationProfilePage/OrganizationSlice'
import authenticationReducer from '../features/authentication/slice/authenticationSlice'
import billingReducer from '../pages/Billing/BillingSlice'
import referReducer from '../features/refer/referSlice'
import { patientsApi } from '../services/patients'
import { prescriptionsApi } from '../services/prescriptions'
import { customPrescriptionItemsApi } from '../services/customPrescriptionItems'
import { patientsBFFApi } from '../services/patientsBFF'
import { integrationApi } from '../features/integration/integrationSlice'
import { locationsApi } from '../features/locations/locationSlice'
import { prescriberLocationProfilesApi } from '../features/locations/prescriberLocationProfilesSlice'
import onboardingContextReducer from '../features/onboardingContext/OnboardingContextSlice'
import patientFormReducer from '../pages/PatientsPage/PatientFormSlice'
import ehrLaunchReducer from '../features/ehr-launch/ehrLaunchSlice'
import { ehrLaunchPatientApi } from '../features/ehr-launch/ehrLaunchPatientApi'
import authenticatedContextReducer from '../features/authenticated-context/authenticatedContextSlice'
import authenticatedProviderReducer from '../features/authentication/slice/authenticatedProviderSlice'
import ehrPatientAcquisitionReducer from '../features/ehr-launch/ehrPatientAcquisitionSlice'
import ehrRecentMedicationRequestsReducer from '../features/ehr-launch/ehrRecentMedicationRequestsSlice'
import { registerPrescriberIdSource } from '../utils/api/prescriberContext'

const rootReducer = combineReducers({
  newScript: scriptReducer,
  organizationAdmin: organizationAdminReducer,
  prescriber: prescriberReducer,
  patientDetail: patientContextReducer,
  clinic: clinicContextReducer,
  product: productSearchContextReducer,
  organization: organizationReducer,
  authentication: authenticationReducer,
  billing: billingReducer,
  refer: referReducer,
  users: organizationUsersReducer,
  onboardingContext: onboardingContextReducer,
  ehrLaunch: ehrLaunchReducer,
  authenticatedContext: authenticatedContextReducer,
  authenticatedProvider: authenticatedProviderReducer,
  ehrPatientAcquisition: ehrPatientAcquisitionReducer,
  ehrRecentMedicationRequests: ehrRecentMedicationRequestsReducer,
  [patientsApi.reducerPath]: patientsApi.reducer,
  [patientsBFFApi.reducerPath]: patientsBFFApi.reducer,
  [prescriptionsApi.reducerPath]: prescriptionsApi.reducer,
  [customPrescriptionItemsApi.reducerPath]: customPrescriptionItemsApi.reducer,
  [integrationApi.reducerPath]: integrationApi.reducer,
  [locationsApi.reducerPath]: locationsApi.reducer,
  [prescriberLocationProfilesApi.reducerPath]: prescriberLocationProfilesApi.reducer,
  [ehrLaunchPatientApi.reducerPath]: ehrLaunchPatientApi.reducer,
  patientForm: patientFormReducer,
})

export type RootState = ReturnType<typeof rootReducer>

export const createAppStore = (preloadedState?: PreloadedState<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(patientsApi.middleware)
        .concat(patientsBFFApi.middleware)
        .concat(prescriptionsApi.middleware)
        .concat(customPrescriptionItemsApi.middleware)
        .concat(integrationApi.middleware)
        .concat(locationsApi.middleware)
        .concat(prescriberLocationProfilesApi.middleware)
        .concat(ehrLaunchPatientApi.middleware),
  })

  registerPrescriberIdSource(() => store.getState().prescriber.prescriber?.id)

  return store
}

export const store = createAppStore()

export type AppDispatch = typeof store.dispatch
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>
